import React, {
  useState,
  useRef,
  useEffect,
  ChangeEvent,
  useCallback,
  forwardRef,
} from 'react'
import styled from 'styled-components'
import zxcvbn from 'zxcvbn'
import debounce from 'debounce'

import { Flex, Text, Form, Input, InputProps } from '@sketch/components'

import { PasswordCriteria } from './PasswordCriteria'
import { PasswordStrengthMeter } from './PasswordStrengthMeter'
import { ToggleShowPasswordButton } from './ToggleShowPasswordButton'

const PasswordTipsArea = styled.div<{ show: boolean }>`
  /*
    Removing this component with JSX is introducing a bug probably related to
    ReactContext. So we're just hidding it with CSS instead of removing.
  */
  display: ${({ show }) => (show ? 'block' : 'none')};
  margin-top: 8px;
`

const StyledPasswordInput = styled(Input)`
  /*
    This overrides the right padding in order to have no text
    under the button to toggle the visibility.
  */
  padding-right: 48px !important; /* stylelint-disable-line declaration-no-important */
`

interface PasswordInputProps extends InputProps {}

/* eslint-disable-next-line react/display-name */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ ...props }: PasswordInputProps, ref) => {
    const [showPassword, setShowPassword] = useState(false)

    return (
      <Flex position="relative">
        <StyledPasswordInput
          type={showPassword ? 'text' : 'password'}
          id="password"
          name="password"
          ref={ref}
          {...props}
        />
        <ToggleShowPasswordButton
          showPassword={showPassword}
          onClick={() => setShowPassword(value => !value)}
          disabled={props.disabled}
        />
      </Flex>
    )
  }
)

interface CreatePasswordInputProps extends InputProps {
  label?: string
}

export const CreatePasswordInput: React.FC<CreatePasswordInputProps> = ({
  label = 'Create a password',
  onChange,
  value,
  ...props
}: CreatePasswordInputProps) => {
  const [password, setPassword] = useState(value?.toString() || '')
  const [showTips, setShowTips] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.addEventListener('focus', () => setShowTips(true))
    }
  }, [inputRef])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updatePassword = useCallback(
    debounce((password: string) => setPassword(password), 250),
    []
  )

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange?.(e)
    updatePassword(e.target.value)
  }

  const passwordStrength = zxcvbn(password)
  const attendToLengthCriteria = password.length >= 8
  const attendToStrengthCriteria = passwordStrength.score >= 2

  return (
    <Form.Field label={label} name="password">
      <PasswordInput ref={inputRef} onChange={handleChange} {...props} />
      <PasswordTipsArea show={showTips}>
        <Text.Span mt={2} mb={0} textStyle="copy.quaternary.standard.C">
          Your password must have:
        </Text.Span>
        <PasswordCriteria
          attendToCriteria={attendToLengthCriteria}
          text="8 or more characters"
        />
        <PasswordCriteria
          attendToCriteria={attendToStrengthCriteria}
          text={
            <>
              <Text.Span
                mr={1}
                color={
                  attendToStrengthCriteria ? 'state.positive.A' : 'sketch.C'
                }
              >
                Okay
              </Text.Span>
              or better strength
            </>
          }
        />
        <PasswordStrengthMeter
          password={password}
          strength={passwordStrength}
        />
      </PasswordTipsArea>
    </Form.Field>
  )
}
